import rawTokens from "./scirix-tokens.json";

export const scirixTokens: typeof rawTokens = rawTokens;

export const gridTokens = scirixTokens.foundation.grid;
const spacingTokens = scirixTokens.foundation.spacing;
const foundationSurfaces = scirixTokens.foundation.surfaces;
const foundationBackground = scirixTokens.foundation.background;
const foundationDimmers = scirixTokens.foundation.dimmers;
const typographyTokens = scirixTokens.foundation.typography;
const semanticSurfaces = scirixTokens.semantic.surface;
const semanticBorders = scirixTokens.semantic.border;
const semanticForeground = scirixTokens.semantic.foreground;
const semanticFocus = scirixTokens.semantic.focus;
const semanticSpecial = scirixTokens.semantic.special;
const semanticLabels = scirixTokens.semantic.labels;
const semanticAccents = scirixTokens.semantic.accents;

export type ScirixTokens = typeof scirixTokens;
export type SemanticStatus = keyof typeof scirixTokens.foundation.semantic;

export const statusPalette = scirixTokens.foundation.semantic;
export const labelPalette = semanticLabels;
export const accentPalette = semanticAccents;

type GridCssVariableName =
  | "--token-grid-columns"
  | "--token-grid-margin-x"
  | "--token-grid-gutter-x"
  | "--token-grid-max-width";

export type GridCssVariables = Record<GridCssVariableName, string>;

const gridTokenValues = scirixTokens.foundation.grid;

export const gridCssVariables: GridCssVariables = {
  "--token-grid-columns": `${gridTokenValues.columns}`,
  "--token-grid-margin-x": `${gridTokenValues.marginX}px`,
  "--token-grid-gutter-x": `${gridTokenValues.gutterX}px`,
  "--token-grid-max-width": `${gridTokenValues.maxWidth}px`,
};

export const pxToRem = (value: number): string => {
  const remValue = value / 16;
  return Number.isInteger(remValue)
    ? `${remValue}rem`
    : `${Number(remValue.toFixed(2))}rem`;
};

const spacingEntries = Object.entries(spacingTokens) as Array<
  [keyof typeof spacingTokens, number]
>;

export const spacingScaleRem = Object.fromEntries(
  spacingEntries.map(([token, value]) => [token, pxToRem(value)])
) as Record<keyof typeof spacingTokens, string>;

/**
 * Legacy baseline spacing tokens map to the closest available Figma spacing value.
 * We round ties upward (e.g., 20px baseline-5 -> xl/24px) and cap the legacy values at xxl.
 * @deprecated Use the canonical spacing tokens (xxs–xxl) instead.
 */
export const legacyBaselineSpacingMap: Record<
  `baseline-${number}`,
  keyof typeof spacingTokens
> = {
  "baseline-1": "xs", // 4px
  "baseline-2": "sm", // 8px
  "baseline-3": "md", // 12px
  "baseline-4": "lg", // 16px
  "baseline-5": "xl", // 20px -> round up to keep padding generous
  "baseline-6": "xl", // 24px
  "baseline-7": "xxl", // 28px -> round up
  "baseline-8": "xxl", // 32px
  "baseline-9": "xxl", // 36px -> capped to largest token
  "baseline-10": "xxl", // 40px -> capped
  "baseline-11": "xxl", // 44px -> capped
  "baseline-12": "xxl", // 48px -> capped
  "baseline-13": "xxl", // 52px -> capped
  "baseline-14": "xxl", // 56px -> capped
  "baseline-15": "xxl", // 60px -> capped
  "baseline-16": "xxl", // 64px -> capped
};

export const legacyBaselineSpacingValues = Object.fromEntries(
  Object.entries(legacyBaselineSpacingMap).map(([legacyToken, mappedToken]) => [
    legacyToken,
    spacingTokens[mappedToken],
  ])
) as Record<`baseline-${number}`, number>;

type ShadowToken =
  (typeof scirixTokens.foundation.shadows)[keyof typeof scirixTokens.foundation.shadows];

const formatShadow = (shadow: ShadowToken): string =>
  `${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.spread}px ${shadow.color}`;

// Emit spacing in rem so paddings/gaps follow the fluid root font-size in
// globals.css and tighten on smaller viewports. The token definitions stay
// in px; pxToRem(16) -> 1rem etc.
const spacingVariables = Object.fromEntries(
  spacingEntries.map(([token, value]) => [
    `--token-spacing-${token}`,
    pxToRem(value),
  ])
) as Record<`--token-spacing-${keyof typeof spacingTokens}`, string>;

const radiusVariables = {
  "--token-radius-none": `${scirixTokens.foundation.radius.none}px`,
  "--token-radius-small": `${scirixTokens.foundation.radius.small}px`,
  "--token-radius-default": `${scirixTokens.foundation.radius.default}px`,
  "--token-radius-large": `${scirixTokens.foundation.radius.large}px`,
  "--token-radius-xl": `${scirixTokens.foundation.radius.xl}px`,
  "--token-radius-xxl": `${scirixTokens.foundation.radius.xxl}px`,
  "--token-radius-giga": `${scirixTokens.foundation.radius.giga}px`,
  "--token-radius-full": `${scirixTokens.foundation.radius.full}px`,
} as const;

const shadowVariables = {
  "--token-shadow-sm": formatShadow(scirixTokens.foundation.shadows.sm),
  "--token-shadow-md": formatShadow(scirixTokens.foundation.shadows.md),
  "--token-shadow-lg": formatShadow(scirixTokens.foundation.shadows.lg),
} as const;

type BaseTypographyStyleName = keyof typeof typographyTokens.styles;
type TypographyStyleName = BaseTypographyStyleName | "h4";

const typographyStyleEntries = Object.entries(typographyTokens.styles) as Array<
  [BaseTypographyStyleName, (typeof typographyTokens.styles)[BaseTypographyStyleName]]
>;

const typographyFontVariables = {
  "--token-font-sans": typographyTokens.fonts.sans,
  "--token-font-mono": typographyTokens.fonts.mono,
} as const;

const typographyWeightVariables = Object.fromEntries(
  Object.entries(typographyTokens.weights).map(([name, weight]) => [
    `--token-font-weight-${name}`,
    `${weight}`,
  ])
) as Record<`--token-font-weight-${keyof typeof typographyTokens.weights}`, string>;

// Emit typography sizes as rem so the fluid root font-size in globals.css
// (`html { font-size: clamp(13px, ..., 16px) }`) scales every text style on
// smaller viewports. Tokens are authored in px; divide by the 16px base to
// get the rem equivalent.
const TYPOGRAPHY_REM_BASE = 16;

const typographySizeVariables = Object.fromEntries(
  typographyStyleEntries.map(([name, style]) => [
    `--token-typography-size-${name}`,
    `${style.fontSize / TYPOGRAPHY_REM_BASE}rem`,
  ])
) as Record<`--token-typography-size-${keyof typeof typographyTokens.styles}`, string>;

const typographyLineHeightVariables = Object.fromEntries(
  typographyStyleEntries.map(([name, style]) => [
    `--token-typography-line-height-${name}`,
    `${style.lineHeight / TYPOGRAPHY_REM_BASE}rem`,
  ])
) as Record<
  `--token-typography-line-height-${keyof typeof typographyTokens.styles}`,
  string
>;

const typographyLetterSpacingVariables = Object.fromEntries(
  Object.entries(typographyTokens.letterSpacing).map(([name, value]) => [
    `--token-letter-spacing-${name}`,
    value,
  ])
) as Record<
  `--token-letter-spacing-${keyof typeof typographyTokens.letterSpacing}`,
  string
>;

// `description` is metadata on a group/family, not a token — skip it both at
// the family level (e.g. `accents.description`) and the variant level (e.g.
// `accents.success.description`).
const isTokenEntry = ([key]: [string, unknown]) => key !== "description";
const isTokenObject = (entry: [string, unknown]): entry is [string, Record<string, string>] =>
  isTokenEntry(entry) && typeof entry[1] === "object" && entry[1] !== null;

const accentVariables = Object.fromEntries(
  Object.entries(semanticAccents)
    .filter(isTokenObject)
    .flatMap(([accentName, accentValues]) =>
      Object.entries(accentValues)
        .filter(isTokenEntry)
        .map(([tokenName, tokenValue]) => [
          `--token-accent-${accentName}-${tokenName.replace(/_/g, "-")}`,
          tokenValue,
        ]),
    ),
) as Record<string, string>;

// Audit role tokens — narrow color assignments for finding-type badges.
// Extend when the audit pipeline grows new finding categories.
const semanticAudit = scirixTokens.semantic.audit;
const auditVariables = Object.fromEntries(
  Object.entries(semanticAudit)
    .filter(isTokenObject)
    .flatMap(([roleName, roleValues]) =>
      Object.entries(roleValues)
        .filter(isTokenEntry)
        .map(([tokenName, tokenValue]) => [
          `--token-audit-${roleName}-${tokenName.replace(/_/g, "-")}`,
          tokenValue,
        ]),
    ),
) as Record<string, string>;

// Canvas overlay tokens — translucent surfaces used by the ideas canvas for
// tag-type tinting. Flat variant map because each entry is a single color.
const semanticCanvas = scirixTokens.semantic.canvas;
const canvasVariables = Object.fromEntries(
  Object.entries(semanticCanvas.overlay)
    .filter(isTokenEntry)
    .map(([variant, value]) => [
      `--token-canvas-overlay-${variant}`,
      value,
    ]),
) as Record<string, string>;

// Categorical series tokens — the brand's DATA COLOR set, one hue per series
// (a repository lane in the Changes timeline). Unlike every family above these
// carry no status meaning, so nothing here should be reached for to signal
// success or failure. Assignment lives in lib/changes/repo-series.ts, which
// pairs each hue with a glyph; colour alone is never the carrier.
const semanticSeries = scirixTokens.semantic.series;
const seriesVariables = Object.fromEntries(
  Object.entries(semanticSeries)
    .filter(isTokenObject)
    .flatMap(([slotName, slotValues]) =>
      Object.entries(slotValues)
        .filter(isTokenEntry)
        .map(([tokenName, tokenValue]) => [
          `--token-series-${slotName}-${tokenName.replace(/_/g, "-")}`,
          tokenValue,
        ]),
    ),
) as Record<string, string>;

// Lifecycle-event tokens — one distinct hue per agent-manager event type so
// scrolling event logs stay scannable. Skips the `description` metadata key.
const semanticEvent = scirixTokens.semantic.event as Record<string, unknown>;
const eventVariables = Object.fromEntries(
  Object.entries(semanticEvent).flatMap(([name, values]) => {
    if (name === "description" || typeof values !== "object" || values === null) {
      return [];
    }
    return Object.entries(values as Record<string, string>).map(([variant, value]) => [
      `--token-event-${name.replace(/_/g, "-")}-${variant.replace(/_/g, "-")}`,
      value,
    ]);
  }),
) as Record<string, string>;

export type TypographyStyle = {
  fontFamily: string;
  fontWeight: number;
  fontSize: string;
  lineHeight: string;
};

const baseTypographyStyles = Object.fromEntries(
  typographyStyleEntries.map(([name, style]) => [
    name,
    {
      fontFamily: style.fontFamily,
      fontWeight: style.fontWeight,
      fontSize: `${style.fontSize / TYPOGRAPHY_REM_BASE}rem`,
      lineHeight: `${style.lineHeight / TYPOGRAPHY_REM_BASE}rem`,
    },
  ])
) as Record<BaseTypographyStyleName, TypographyStyle>;

const h4SourceStyleKey: BaseTypographyStyleName = "body-medium";

export const typographyStyles = {
  ...baseTypographyStyles,
  h4: { ...baseTypographyStyles[h4SourceStyleKey] },
} as Record<TypographyStyleName, TypographyStyle>;

export const typographySizesPx = Object.fromEntries(
  Object.entries(typographyTokens.sizes).map(([name, value]) => [
    name,
    `${value}px`,
  ])
) as Record<keyof typeof typographyTokens.sizes, string>;

export const typographyLineHeightsPx = Object.fromEntries(
  Object.entries(typographyTokens.lineHeights).map(([name, value]) => [
    name,
    `${value}px`,
  ])
) as Record<keyof typeof typographyTokens.lineHeights, string>;

const gridLayoutVariables = {
  "--token-grid-sidebar": `${gridTokens.sidebar}px`,
  "--token-grid-detail-min": `${gridTokens.detailMin}px`,
  "--token-grid-detail-max": `${gridTokens.detailMax}px`,
  "--token-grid-topbar": `${gridTokens.topbar}px`,
} as const;

export const themeCssVariables = {
  // Foundation surfaces and backgrounds
  "--token-foundation-surface-base": foundationSurfaces.surface_base,
  "--token-foundation-surface-subtle": foundationSurfaces.surface_subtle,
  "--token-foundation-surface-subtle-hover": foundationSurfaces.surface_subtle_hover,
  "--token-foundation-surface-subtle-active": foundationSurfaces.surface_subtle_active,
  "--token-foundation-surface-pop": foundationSurfaces.surface_pop,
  "--token-foundation-surface-pop-hover": foundationSurfaces.surface_pop_hover,
  "--token-foundation-surface-pop-active": foundationSurfaces.surface_pop_active,
  "--token-foundation-background-canvas": foundationBackground.canvas,
  "--token-foundation-dimmer-base": foundationDimmers.dimmer_base,
  "--token-foundation-dimmer-panel": foundationDimmers.dimmer_panel,

  // Semantic surfaces (one canonical rest per role; interaction states come
  // from `state.hover` / `active` overlays layered on top).
  "--token-surface-default": semanticSurfaces.default.rest,
  "--token-surface-muted": semanticSurfaces.muted.rest,
  "--token-surface-primary": semanticSurfaces.primary.rest,
  "--token-surface-success": semanticSurfaces.success.rest,
  "--token-surface-warning": semanticSurfaces.warning.rest,
  "--token-surface-danger": semanticSurfaces.danger.rest,
  "--token-surface-info": semanticSurfaces.info.rest,

  // Semantic borders — neutral tiers. For intent-tinted borders, use the
  // per-family accent border tokens (see the accent block above) instead.
  "--token-border-default": semanticBorders.default,
  "--token-border-muted": semanticBorders.muted,
  "--token-border-strong": semanticBorders.strong,

  // Semantic foreground — intent variants collapse to the core foreground.
  "--token-foreground-default": semanticForeground.default,
  "--token-foreground-muted": semanticForeground.muted,
  "--token-foreground-primary": semanticForeground.primary,
  "--token-foreground-success": semanticForeground.success,
  "--token-foreground-warning": semanticForeground.warning,
  "--token-foreground-danger": semanticForeground.danger,
  "--token-foreground-info": semanticForeground.info,

  // Focus rings
  "--token-focus-primary": semanticFocus.primary,

  // Special surfaces
  "--token-surface-scrim": semanticSpecial.surface_scrim,

  // Legacy aliases (to be removed once all components migrate)
  "--token-foreground": semanticForeground.default,
  "--token-accent": semanticSurfaces.primary.rest,
  "--token-accent-soft": foundationSurfaces.surface_pop,
  "--token-success": semanticSurfaces.success.rest,
  "--token-danger": semanticSurfaces.danger.rest,
  "--token-primary-foreground": foundationSurfaces.surface_base,

  // Legacy status palette kept for compatibility while migrating
  "--token-status-bug": statusPalette.bug,
  "--token-status-critical": statusPalette.critical,
  "--token-status-high": statusPalette.high,
  "--token-status-feature": statusPalette.feature,
  "--token-status-design": statusPalette.design,
  "--token-status-enhancement": statusPalette.enhancement,
  "--token-status-reviewed": statusPalette.reviewed,
  "--token-status-confirmed": statusPalette.confirmed,
  "--token-status-foreground-light": statusPalette.foreground.light,
  "--token-status-foreground-dark": statusPalette.foreground.dark,

  // Typography
  ...typographyFontVariables,
  ...typographyWeightVariables,
  ...typographySizeVariables,
  ...typographyLineHeightVariables,
  ...typographyLetterSpacingVariables,

  // Derived helpers
  ...spacingVariables,
  ...radiusVariables,
  ...shadowVariables,
  ...gridLayoutVariables,
  ...accentVariables,
  ...auditVariables,
  ...seriesVariables,
  ...canvasVariables,
  ...eventVariables,
} as Record<string, string>;

export const gridTokensPx = {
  columns: gridTokens.columns,
  marginX: `${gridTokens.marginX}px`,
  gutterX: `${gridTokens.gutterX}px`,
  sidebar: `${gridTokens.sidebar}px`,
  detailMin: `${gridTokens.detailMin}px`,
  detailMax: `${gridTokens.detailMax}px`,
  topbar: `${gridTokens.topbar}px`,
} as const;

export const layoutCssVars = {
  gridColumns: gridTokens.columns.toString(),
  gridMarginX: `${gridTokens.marginX}px`,
  gridGutterX: `${gridTokens.gutterX}px`,
} as const;
